import { ProductFeatureName } from 'dashboard/models/organization';
import { Verification } from 'dashboard/models/verification';
import {
  createPreviewVerificationAamva,
  createPreviewVerificationDatabase,
  createPreviewVerificationDatabaseStandard,
  createPreviewVerificationDocument,
  createPreviewVerificationEmailAddress,
  createPreviewVerificationGovernmentId,
  createPreviewVerificationPhoneNumber,
  createPreviewVerificationSelfie,
} from 'dashboard/modules/CaseModuleBase/previewBuilders/verification';
import { ApiModel, ApiOptionalRelationship, ApiResource } from 'lib/interfaces/api';

// When adding to this list, please also ensure they are added to VerificationTemplateBuilder
export enum BuilderName {
  Aamva = 'aamva',
  Database = 'database',
  DatabaseStandard = 'database_standard',
  Document = 'document',
  EmailAddress = 'email_address',
  GovernmentId = 'government_id',
  PhoneNumber = 'phone_number',
  Selfie = 'selfie',
}
export interface VerificationTemplateBuilders {
  'supported-builders': VerificationTemplateBuilder[];
}

export interface VerificationTemplateBuilder {
  id: BuilderName;
  name: string;
  description: string;
  'help-center-article': string;
  type: VerificationTemplate['type'];
  'required-product-features': ProductFeatureName[];
  'requires-order-form': boolean;
  'requires-manual-support': boolean;
}

export const VERIFICATION_BUILDER_NAME_TO_PREVIEW: Record<string, Verification> = {
  [BuilderName.Aamva]: createPreviewVerificationAamva(),
  [BuilderName.Database]: createPreviewVerificationDatabase(),
  [BuilderName.DatabaseStandard]: createPreviewVerificationDatabaseStandard(),
  [BuilderName.Document]: createPreviewVerificationDocument(),
  [BuilderName.EmailAddress]: createPreviewVerificationEmailAddress(),
  [BuilderName.GovernmentId]: createPreviewVerificationGovernmentId(),
  [BuilderName.PhoneNumber]: createPreviewVerificationPhoneNumber(),
  [BuilderName.Selfie]: createPreviewVerificationSelfie(),
};

export function activeVerificationTemplate(template: VerificationTemplate): boolean {
  return template.attributes.status === VerificationTemplateStatus.Active;
}

export enum VerificationTemplateStatus {
  Active = 'active',
  Inactive = 'inactive',
}

export interface VerificationTemplateAttributes {
  status: VerificationTemplateStatus;
  name: string;

  'created-at': string;
  'updated-at': string;
  'archived-at'?: string;
}

export interface VerificationTemplateRelationships {
  'latest-published-version': ApiOptionalRelationship<ApiModel.VerificationTemplateVersion>;
  'latest-draft-version': ApiOptionalRelationship<ApiModel.VerificationTemplateVersion>;
}

export interface VerificationTemplateFilterOption {
  id: string;
  name: string;
  type: VerificationTemplate['type'];
  status?: VerificationTemplateStatus;
  'updated-at': string;
}

export interface VerificationTemplateFiltersMeta {
  filters: VerificationTemplateFilterOption[];
}

export interface VerificationTemplateFiltersResponse {
  meta: VerificationTemplateFiltersMeta;
}

export type VerificationTemplateAamva = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateAamva
>;
export type VerificationTemplateBusinessWebsite = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateBusinessWebsite
>;
export type VerificationTemplateCertificateKorea = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateCertificateKorea
>;
export type VerificationTemplateDatabase = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDatabase
>;
export type VerificationTemplateDatabaseAadhaar = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDatabaseAadhaar
>;
export type VerificationTemplateDatabaseBusiness = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDatabaseBusiness
>;
export type VerificationTemplateDatabaseBusinessFootprint = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDatabaseBusinessFootprint
>;
export type VerificationTemplateDatabaseEcbsv = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDatabaseEcbsv
>;
export type VerificationTemplateDatabasePhoneCarrier = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDatabasePhoneCarrier
>;
export type VerificationTemplateDatabaseSerpro = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDatabaseSerpro
>;
export type VerificationTemplateDatabaseStandard = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDatabaseStandard
>;
export type VerificationTemplateDatabaseTin = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDatabaseTin
>;
export type VerificationTemplateDatabaseVatNumber = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDatabaseVatNumber
>;
export type VerificationTemplateDigitalIdFinnishTrustNetwork = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDigitalIdFinnishTrustNetwork
>;
export type VerificationTemplateDigitalIdOneId = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDigitalIdOneId
>;
export type VerificationTemplateDigitalIdSingpass = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDigitalIdSingpass
>;
export type VerificationTemplateDigitalIdUkSharecode = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDigitalIdUkSharecode
>;
export type VerificationTemplateDocument = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateDocument
>;
export type VerificationTemplateEmailAddress = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateEmailAddress
>;
export type VerificationTemplateGovernmentId = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateGovernmentId
>;
export type VerificationTemplateGovernmentIdNfc = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateGovernmentIdNfc
>;
export type VerificationTemplateMobileDriversLicense = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateMobileDriversLicense
>;
export type VerificationTemplatePhoneNumber = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplatePhoneNumber
>;
export type VerificationTemplateSelfie = ApiResource<
  VerificationTemplateAttributes,
  VerificationTemplateRelationships,
  ApiModel.VerificationTemplateSelfie
>;
export type VerificationTemplate =
  | VerificationTemplateAamva
  | VerificationTemplateBusinessWebsite
  | VerificationTemplateCertificateKorea
  | VerificationTemplateDatabase
  | VerificationTemplateDatabaseAadhaar
  | VerificationTemplateDatabaseStandard
  | VerificationTemplateDatabaseBusiness
  | VerificationTemplateDatabaseBusinessFootprint
  | VerificationTemplateDatabaseEcbsv
  | VerificationTemplateDatabasePhoneCarrier
  | VerificationTemplateDatabaseSerpro
  | VerificationTemplateDatabaseTin
  | VerificationTemplateDatabaseVatNumber
  | VerificationTemplateDigitalIdFinnishTrustNetwork
  | VerificationTemplateDigitalIdOneId
  | VerificationTemplateDigitalIdSingpass
  | VerificationTemplateDigitalIdUkSharecode
  | VerificationTemplateDocument
  | VerificationTemplateEmailAddress
  | VerificationTemplateGovernmentId
  | VerificationTemplateGovernmentIdNfc
  | VerificationTemplateMobileDriversLicense
  | VerificationTemplatePhoneNumber
  | VerificationTemplateSelfie;
